import { Injectable } from '@angular/core';
import { map, Observable, OperatorFunction } from 'rxjs';

import { RequestService } from '../request';
import {
  IGetPatientListRequest,
  IGetPatientListByRequest,
  IPatientHistory,
  IPatientRequest,
  IPatientRequestDetail,
} from './defs';
import {
  EpidemiologyResponse,
  EpidemiologySearchParams,
  SensitivityATBResponse,
} from './epidemiology.types';
import { EpidemiologyError } from './epidemiology-error';
import { UsersService } from '../users';
import { EncryptionService } from '../common/services/encryption.service';
import { AuthService } from '../auth';
import { formatDateAsYyyyMmDdLocal } from '@app/core/utils/format-local-iso-date';

const REQUEST_SEARCH_KEY = 'PatientRequestSearch';
const PATIENT_SEARCH_KEY = 'PatientSearch';

@Injectable({
  providedIn: 'root',
})
export class PatientRequestsService {
  constructor(
    private request: RequestService,
    private userService: UsersService,
    private encryptionService: EncryptionService,
    private authService: AuthService
  ) {}

  getEncriptionKey(): string {
    const user = this.authService.getUsername()?.userName || '';
    return `${user}12345678901234567890123456789012`;
  }
  /**
   * Get previously executed request search
   */
  public getPreviousRequestSearch(): any {
    const value = sessionStorage.getItem(REQUEST_SEARCH_KEY);
    // Decrypt the value before parsing
    const decryptedValue = this.encryptionService.decrypt(
      value,
      this.getEncriptionKey()
    );
    return decryptedValue;
  }

  /**
   * Get previously executed patient search
   */
  public getPreviousPatientSearch(): IGetPatientListRequest {
    const value = sessionStorage.getItem(PATIENT_SEARCH_KEY);
    // Decrypt the value before parsing
    const decryptedValue = this.encryptionService.decrypt(
      value,
      this.getEncriptionKey()
    );
    return decryptedValue;
  }

  /**
   * Store executed request search
   */
  public setRequestSearch($event: any) {
    // Encrypt the event data
    const encryptedData = this.encryptionService.encrypt(
      $event,
      this.getEncriptionKey()
    );
    sessionStorage.setItem(REQUEST_SEARCH_KEY, encryptedData);
  }

  /**
   * Store executed patient search
   */
  public setPatientSearch($event: any) {
    // Encrypt the event data
    const encryptedData = this.encryptionService.encrypt(
      $event,
      this.getEncriptionKey()
    );
    sessionStorage.setItem(PATIENT_SEARCH_KEY, encryptedData);
  }

  public getPatientHistories(
    request: IGetPatientListRequest
  ): Observable<IPatientHistory[]> {
    const centroId = request.centroId ?? this.userService.getSisifoDefaultCenter();
    const params: Record<string, unknown> = { ...request, centroId };
    for (const key of ['extractionFrom', 'extractionTo', 'birthDate'] as const) {
      const v = (request as Record<string, unknown>)[key];
      if (v == null || v === '') continue;
      const normalized = this.normalizeHistoryDateQueryParam(v);
      if (normalized != null) {
        params[key] = normalized;
      }
    }
    return this.request.get('/api/patient-requests/histories', params);
  }

  /**
   * Fecha de calendario local en YYYY-MM-DD (issue #532 / wsp_Search_Patient).
   * Evita que Date serializado como ISO en GET desplace un día al interpretarlo en UTC en Node.
   */
  private normalizeHistoryDateQueryParam(value: unknown): string | null {
    if (value instanceof Date) {
      return formatDateAsYyyyMmDdLocal(value);
    }
    if (typeof value === 'string') {
      const t = value.trim();
      if (!t) return null;
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(t)) return t;
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
      const d = new Date(t);
      if (!Number.isNaN(d.getTime())) {
        return formatDateAsYyyyMmDdLocal(d);
      }
    }
    return null;
  }

  public getParameters(): Observable<any[]> {
    return this.request.get('/api/patient-requests/parameters');
  }

  public getPatientRequests(
    request: IGetPatientListByRequest
  ): Observable<IPatientRequest[]> {
    const centroId = request.centroId ?? this.userService.getSisifoDefaultCenter();
    return this.request.get('/api/patient-requests/requests', {
      ...request,
      centroId,
    }).pipe(this.formatDates<IPatientRequest>(['einDat']));
  }

  public searchRequests(
    request: IGetPatientListByRequest
  ): Observable<IPatientRequest[]> {
    // map request to convert all materialName in materialname
    return this.request
      .get('/api/patient-requests/search', {
        ...request,
        centroId: this.userService.getSisifoDefaultCenter(),
      })
      .pipe(
        map((requests: IPatientRequest[]) => {
          return requests.map((request) => {
            request.materialname = request.materialName;
            return request;
          });
        }),
        this.formatDates<IPatientRequest>(['einDat'])
      );
  }

  /**
   * Transform specific field objects to dates
   */
  private formatDates<T>(fields: string[]): OperatorFunction<T[], T[]> {
    return map((response: T[]) => {
      response.forEach(element => {
        fields.forEach(field => element[field] = element[field] ? new Date(element[field]) : null);
      });
      return response;
    });
  }

  public generateRequestPDF(
    requisitionNumber: number,
    entryDate: string,
    labNumber: number,
    patientId: string
  ): Observable<any> {
    return this.request.post('/api/patient-requests/print', {
      requisitionNumber,
      entryDate,
      labNumber,
      patientId,
    });
  }

  /**
   * Search acumulados for a patient request (wspi_Search_Cumu).
   * @param params patientId, labNr, referenceDate (ISO or dd/mm/yyyy), optional requisType, testList (comma-separated codes), graphicFormat (0|1)
   */
  public searchAcumulados(params: {
    patientId: string;
    labNr: number;
    referenceDate: string;
    requisType?: number;
    testList?: string;
    graphicFormat?: number;
    centroId?: number | null;
  }): Observable<{ data?: unknown[] }> {
    const centroId = params.centroId ?? this.userService.getSisifoDefaultCenter();
    return this.request.get('/api/patient-requests/acumulados', {
      ...params,
      centroId,
    }) as Observable<{ data?: unknown[] }>;
  }

  /**
   * wspi_Search_Epidemiology (mock or SP). Throws {@link EpidemiologyError} when msgType &gt; 0.
   */
  public searchEpidemiology(params: EpidemiologySearchParams): Observable<EpidemiologyResponse> {
    const centroId = params.centroId ?? this.userService.getSisifoDefaultCenter();
    return this.request
      .get<EpidemiologyResponse>('/api/patient-requests/epidemiology', {
        ...params,
        centroId,
      })
      .pipe(
        map((res) => {
          if (res.msgType > 0 && res.msgCode) {
            throw new EpidemiologyError(res.msgCode, res.msgType);
          }
          return res;
        })
      );
  }

  /**
   * wspi_Search_SensitivityATB (mock or SP). Throws {@link EpidemiologyError} when msgType &gt; 0.
   */
  public searchSensitivityATB(params: EpidemiologySearchParams): Observable<SensitivityATBResponse> {
    const centroId = params.centroId ?? this.userService.getSisifoDefaultCenter();
    return this.request
      .get<SensitivityATBResponse>('/api/patient-requests/sensitivity-atb', {
        ...params,
        centroId,
      })
      .pipe(
        map((res) => {
          if (res.msgType > 0 && res.msgCode) {
            throw new EpidemiologyError(res.msgCode, res.msgType);
          }
          return res;
        })
      );
  }

  /**
   * Get requisition types for acumulados filter (dropdown).
   */
  public getRequisTypes(): Observable<{ codRequisType: number; requisType: string }[]> {
    return this.request.get('/api/patient-requests/acumulados/requis-types') as Observable<{ codRequisType: number; requisType: string }[]>;
  }

  /**
   * Patient tests for a lab (wspi_Search_PatientTests). For dialog 2.2 PickList "Técnicas disponibles".
   * Envía centro de sesión; el SP exige centro válido (p. ej. USER_NO_CENTER si falta).
   */
  public getPatientTests(patientId: string, labNr: number): Observable<{ data: { methNr: number; methName: string }[]; msgCode?: string; msgType?: number }> {
    return this.request.get('/api/patient-requests/patient-tests', {
      patientId: patientId.trim(),
      labNr,
      centroId: this.userService.getSisifoDefaultCenter(),
    }) as Observable<{ data: { methNr: number; methName: string }[]; msgCode?: string; msgType?: number }>;
  }

  /**
   * LabNr from labor name. For dialog 2.2 when lab options only have labName.
   */
  public getLabNrByLaborName(laborName: string): Observable<{ labNr: number | null }> {
    return this.request.get('/api/patient-requests/lab-nr', {
      laborName: laborName.trim(),
    }) as Observable<{ labNr: number | null }>;
  }

  public getPatientRequestDetail(
    laboratory: string,
    idRequest: string,
    laboratoryNumber: number,
    date: string,
    impreso: string,
    sitio: string,
    centroId?: number | null
  ): Observable<IPatientRequestDetail> {
    // Codificar cada componente de la URL
    const encodedLaboratory = encodeURIComponent(
      laboratory.slice(0, 3).toUpperCase()
    );
    const encodedIdRequest = encodeURIComponent(idRequest);
    const encodedLaboratoryNumber = encodeURIComponent(
      laboratoryNumber.toString()
    );
    const encodedDate = encodeURIComponent(date);
    const encodedImpreso = encodeURIComponent(impreso === '.' ? '-' : impreso);

    // Construir la URL con los componentes codificados
    const url = `/api/patient-requests/requests/${encodedLaboratory}/${encodedIdRequest}/${encodedLaboratoryNumber}/${encodedDate}/${encodedImpreso}/${sitio}`;

    return this.request.get<IPatientRequestDetail>(url, {
      centroId: centroId ?? this.userService.getSisifoDefaultCenter(),
    });
  }
}
